import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Images } from '../../../utils/imageConstants';
import { personalDetailsSchema, type PersonalDetailsFormValues } from '../validation/personalDetailsSchema';
import type { ProfileOnboardingLocationState, Gender } from '../types/profileOnboarding.d';
import { ProfileDatePicker } from '../components/ProfileDatePicker';
import '../PersonalDetailsStep.scss';

type PersonalDraft = PersonalDetailsFormValues & { profileImage?: File | null };

interface PersonalDetailsStepProps {
  locationState: ProfileOnboardingLocationState;
  initialPersonal?: PersonalDraft;
  onNext: (data: PersonalDetailsFormValues, profileImage: File | null) => void;
  onRolesChange?: (isPlayer: boolean, isUmpire: boolean, isScorer: boolean) => void;
}

const GENDERS: Gender[] = ['Male', 'Female', 'Other'];

function PersonalDetailsStep({
  locationState,
  initialPersonal,
  onNext,
  onRolesChange,
}: PersonalDetailsStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { email, mobileNumber = '', countryCode = 91 } = locationState;

  const { register, handleSubmit, formState: { errors, isValid, dirtyFields }, watch, setValue, reset } = useForm<PersonalDetailsFormValues>({
    resolver: yupResolver(personalDetailsSchema) as Resolver<PersonalDetailsFormValues>,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: initialPersonal?.firstName ?? '',
      middleName: initialPersonal?.middleName ?? '',
      lastName: initialPersonal?.lastName ?? '',
      dateOfBirth: initialPersonal?.dateOfBirth ?? '',
      gender: GENDERS.includes((initialPersonal?.gender ?? '') as Gender)
        ? (initialPersonal!.gender as PersonalDetailsFormValues['gender'])
        : undefined,
      isPlayer: initialPersonal?.isPlayer ?? false,
      isUmpire: initialPersonal?.isUmpire ?? false,
      isScorer: initialPersonal?.isScorer ?? false,
    },
  });

  useEffect(() => {
    if (!initialPersonal) return;
    const { profileImage, ...rest } = initialPersonal;
    void profileImage;
    reset({
      firstName: rest.firstName,
      middleName: rest.middleName,
      lastName: rest.lastName,
      dateOfBirth: rest.dateOfBirth,
      gender: rest.gender,
      isPlayer: rest.isPlayer,
      isUmpire: rest.isUmpire,
      isScorer: rest.isScorer,
    });
  }, [initialPersonal, reset]);

  const isPlayer = watch('isPlayer');
  const isUmpire = watch('isUmpire');
  const isScorer = watch('isScorer');
  const gender = watch('gender');
  const dateOfBirth = watch('dateOfBirth');

  useEffect(() => {
    onRolesChange?.(isPlayer, isUmpire, isScorer);
  }, [isPlayer, isUmpire, isScorer, onRolesChange]);

  const handleAvatarClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleAvatarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    if (profileImageUrl) URL.revokeObjectURL(profileImageUrl);
    const url = URL.createObjectURL(file);
    setProfileImageUrl(url);
    setProfileImageFile(file);
  }, [profileImageUrl]);

  const onSubmit = async (values: PersonalDetailsFormValues) => {
    setIsSubmitting(true);
    try {
      onNext(values, profileImageFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="personal-details-form">
      <div className="profile-avatar-wrap">
        <div className="profile-avatar" onClick={handleAvatarClick}>
          {profileImageUrl ? (
            <img src={profileImageUrl} alt="Profile" />
          ) : (
            <img src={Images.playerProfileImage} alt="Profile placeholder" />
          )}
        </div>
        <button
          type="button"
          className="profile-avatar-camera"
          onClick={handleAvatarClick}
          aria-label="Upload profile image"
        >
          <span aria-hidden>
            <img src={Images.cameraIcon} alt="Camera" />
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={handleAvatarChange}
        />
      </div>

      <div className="personal-details-grid personal-details-row1">
        <div className="personal-details-field">
          <label htmlFor="firstName">First Name <span className="req">*</span></label>
          <input
            id="firstName"
            type="text"
            placeholder="Enter first name"
            className={errors.firstName ? 'error' : ''}
            {...register('firstName')}
          />
          {errors.firstName && (
            <span className="field-error">{errors.firstName.message}</span>
          )}
        </div>
        <div className="personal-details-field">
          <label htmlFor="middleName">Middle Name</label>
          <input
            id="middleName"
            type="text"
            placeholder="Enter middle name"
            {...register('middleName')}
          />
        </div>
      </div>

      <div className="personal-details-grid personal-details-row2">
        <div className="personal-details-field">
          <label htmlFor="lastName">Last Name <span className="req">*</span></label>
          <input
            id="lastName"
            type="text"
            placeholder="Enter last name"
            className={errors.lastName ? 'error' : ''}
            {...register('lastName')}
          />
          {errors.lastName && (
            <span className="field-error">{errors.lastName.message}</span>
          )}
        </div>
        <div className="personal-details-field">
          <label htmlFor="dateOfBirth">Date of Birth <span className="req">*</span></label>
          <ProfileDatePicker
            id="dateOfBirth"
            value={dateOfBirth ?? ''}
            onChange={(v) =>
              setValue('dateOfBirth', v, { shouldValidate: true, shouldDirty: true })
            }
            placeholder="Select date of birth"
            hasError={!!errors.dateOfBirth}
            disableFuture
          />
          {errors.dateOfBirth && (
            <span className="field-error">{errors.dateOfBirth.message}</span>
          )}
        </div>

      </div>

      <div className="personal-details-grid personal-details-row3">
        <div className="personal-details-field">
          <label>Mobile Number <span className="req">*</span></label>
          <div className="readonly-field">
            <span className="country-code">(+{countryCode})</span>
            <span className="readonly-value">{mobileNumber || '—'}</span>
          </div>
        </div>
        <div className="personal-details-field">
          <label>Email <span className="req">*</span></label>
          <div className="readonly-field">
            <span className="readonly-value">{email || '—'}</span>
          </div>
        </div>
      </div>

      <div className="personal-details-field gender-field">
        <label>Gender <span className="req">*</span></label>
        <div className="gender-buttons">
          {GENDERS.map((g) => (
            <button
              key={g}
              type="button"
              className={`gender-btn ${gender === g ? 'active' : ''}`}
              onClick={() => setValue('gender', g, { shouldValidate: true, shouldDirty: true })}
            >
              {g}
            </button>
          ))}
        </div>
        {errors.gender && dirtyFields.gender && (
          <span className="field-error">{errors.gender.message}</span>
        )}
      </div>

      <div className="role-registration">
        <div className="personal-details-field create-league-toggle">
          <div className="create-league-toggle-copy">
            <span className="create-league-step-label">Register as a Player</span>
          </div>
          <button
            type="button"
            className={`create-league-switch ${isPlayer ? 'on' : ''}`}
            onClick={() => setValue('isPlayer', !isPlayer, { shouldValidate: true, shouldDirty: true })}
            aria-label="Toggle register as player"
          >
            <span className="create-league-switch-slider" />
          </button>
        </div>
        <div className="personal-details-field create-league-toggle">
          <div className="create-league-toggle-copy">
            <span className="create-league-step-label">Register as an Umpire</span>
            <p className="create-league-toggle-desc">
              If you&apos;re registered as Umpire, you will be shown as Umpire.
            </p>
          </div>
          <button
            type="button"
            className={`create-league-switch ${isUmpire ? 'on' : ''}`}
            onClick={() => setValue('isUmpire', !isUmpire, { shouldValidate: true, shouldDirty: true })}
            aria-label="Toggle register as umpire"
          >
            <span className="create-league-switch-slider" />
          </button>
        </div>
        <div className="personal-details-field create-league-toggle">
          <div className="create-league-toggle-copy">
            <span className="create-league-step-label">Register as a Scorer</span>
          </div>
          <button
            type="button"
            className={`create-league-switch ${isScorer ? 'on' : ''}`}
            onClick={() => setValue('isScorer', !isScorer, { shouldValidate: true, shouldDirty: true })}
            aria-label="Toggle register as scorer"
          >
            <span className="create-league-switch-slider" />
          </button>
        </div>
      </div>

      <div className="personal-details-actions">
        <button type="submit" className="btn-continue" disabled={isSubmitting || !isValid}>
          {isSubmitting ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </form>
  );
}

export default PersonalDetailsStep;
